// SPDX-License-Identifier: GPL-3.0-or-later
// The perk draft panel. It shows when the run phase is "draft". The player picks 1 of up to 3
// perk cards with keys 1, 2, 3 (also the numpad), a click, a tap or the pad. The panel never picks
// by itself: the run director owns the auto-pick of a featured turn. The panel only shows the
// countdown. All text goes in with `textContent`. The keys work only while the panel shows.
// A click on a card does not pick in the first DRAFT_ARM_MS of a draft. The plunger confirm
// (Space, Enter, the pad plunger) also waits for that window. A flipper tap or a plunger press
// that lands on the panel right after the draft opens must not pick a card by accident. Keys 1, 2
// and 3 pick at once because they name a card. Cursor moves are instant.

import type { InputAction } from "../src/shared/contracts.ts";
import { AUTO_PICK_MS } from "../src/table/run/draft.ts";
import { PERKS } from "../src/table/run/perks.ts";
import type { DraftIndex, PerkDef, RunClientPort, RunView } from "../src/table/run/types.ts";
import { autoPickLine, perkCard } from "./run-text.ts";
import { DRAFT_PANEL_CLASS } from "./touch-surface.ts";

/** The odds line. It is always visible. */
export const ODDS_LINE = "3 perks, drawn evenly from the perks you can still take.";

/**
 * The arm window of a draft, in game milliseconds. A click on a card and a plunger confirm do
 * nothing before this time. The run counts the time of the draft, so the window ends after this
 * much play time.
 */
export const DRAFT_ARM_MS = 500;

/** The part of the run view that the panel reads. */
export type DraftViewLike = Pick<RunView, "phase" | "stageIndex" | "stacks" | "draft">;

/** The part of an element that the panel uses. */
export interface DraftEl {
  hidden: boolean;
  textContent: string | null;
  className: string;
  type?: string;
  disabled?: boolean;
  setAttribute(name: string, value: string): void;
  append(...nodes: DraftEl[]): void;
  addEventListener(type: string, listener: () => void): void;
  focus(): void;
}

/** The key event that the panel reads. */
export interface DraftKeyEvent {
  readonly code: string;
  readonly repeat: boolean;
  preventDefault(): void;
  stopImmediatePropagation(): void;
}

/** The document part that the panel uses. */
export interface DraftDoc {
  readonly activeElement: unknown;
  createElement(tag: string): DraftEl;
  addEventListener(type: "keydown", listener: (ev: DraftKeyEvent) => void, capture: boolean): void;
  removeEventListener(
    type: "keydown",
    listener: (ev: DraftKeyEvent) => void,
    capture: boolean,
  ): void;
}

/** What the page gives the panel. */
export interface DraftPanelDeps {
  readonly doc: DraftDoc;
  /** The element under the playfield that holds the panel. */
  readonly parent: Pick<DraftEl, "append">;
  readonly port: Pick<RunClientPort, "pick">;
  /** True while a modal dialog is open. The keys 1, 2 and 3 do nothing then. */
  readonly modalOpen?: () => boolean;
}

/** The draft panel. */
export interface DraftPanel {
  /** Show or hide the panel from the run view. The page calls it for each frame. */
  render(view: DraftViewLike): void;
  /** A pad or touch action. True when the panel took it. Call it on the press only. */
  action(action: InputAction): boolean;
  /** True while the panel shows. */
  readonly shown: boolean;
}

const KEY_INDEX: Readonly<Record<string, DraftIndex>> = {
  Digit1: 0,
  Numpad1: 0,
  Digit2: 1,
  Numpad2: 1,
  Digit3: 2,
  Numpad3: 2,
};

/** One card: its button and its text nodes. */
interface Card {
  readonly button: DraftEl;
  readonly label: DraftEl;
  readonly text: DraftEl;
  readonly owned: DraftEl;
}

const INDEXES: readonly DraftIndex[] = [0, 1, 2];

function find(id: string | null): PerkDef | undefined {
  return id === null ? undefined : PERKS.find((p) => p.id === id);
}

/** The nodes of the panel. */
interface Parts {
  readonly root: DraftEl;
  readonly title: DraftEl;
  readonly countdown: DraftEl;
  readonly cards: readonly Card[];
}

/** The state of the panel and the things it needs. */
interface Ctx {
  readonly deps: DraftPanelDeps;
  readonly parts: Parts;
  onKey: (ev: DraftKeyEvent) => void;
  cursor: DraftIndex;
  isShown: boolean;
  /** The time of the draft that the last render gave. */
  elapsedMs: number;
  earlier: unknown;
  slots: readonly (PerkDef | undefined)[];
  lastKey: string;
  lastCountdown: string;
}

function makeEl(doc: DraftDoc, tag: string, className: string): DraftEl {
  const el = doc.createElement(tag);
  el.className = className;
  return el;
}

function pick(ctx: Ctx, index: DraftIndex): boolean {
  if (!ctx.isShown || ctx.slots[index] === undefined) return false;
  return ctx.deps.port.pick(index);
}

/**
 * True once the draft has run past the arm window. A card click and a plunger confirm wait for
 * it. Keys 1, 2 and 3 and the cursor moves do not.
 */
function isArmed(ctx: Ctx): boolean {
  return ctx.elapsedMs >= DRAFT_ARM_MS;
}

/** A click on a card. It does nothing inside the arm window. Keys and the flippers do not use this. */
function clickCard(ctx: Ctx, index: DraftIndex): void {
  if (!isArmed(ctx)) return;
  pick(ctx, index);
}

function makeCard(ctx: Ctx, index: DraftIndex): Card {
  const doc = ctx.deps.doc;
  const button = makeEl(doc, "button", "draft-card");
  button.type = "button";
  const key = makeEl(doc, "span", "draft-key");
  key.textContent = String(index + 1);
  const label = makeEl(doc, "span", "draft-label");
  const text = makeEl(doc, "span", "draft-text");
  const owned = makeEl(doc, "span", "draft-owned");
  button.append(key, label, text, owned);
  button.addEventListener("click", () => clickCard(ctx, index));
  button.addEventListener("focus", () => {
    ctx.cursor = index;
  });
  return { button, label, text, owned };
}

function handleKey(ctx: Ctx, ev: DraftKeyEvent): void {
  if (ctx.deps.modalOpen?.() === true) return;
  const index = KEY_INDEX[ev.code];
  if (index === undefined || ctx.slots[index] === undefined) return;
  ev.preventDefault();
  ev.stopImmediatePropagation();
  if (!ev.repeat) pick(ctx, index);
}

/** The nearest card with a perk from `from`, in the direction `step`. -1 when none. */
function neighbour(ctx: Ctx, from: number, step: 1 | -1): number {
  for (let i = from + step; i >= 0 && i < INDEXES.length; i += step) {
    if (ctx.slots[i] !== undefined) return i;
  }
  return -1;
}

function moveCursor(ctx: Ctx, step: 1 | -1): void {
  const next = neighbour(ctx, ctx.cursor, step);
  if (next < 0) return;
  ctx.cursor = next as DraftIndex;
  ctx.parts.cards[ctx.cursor]?.button.focus();
}

function fillCard(card: Card, def: PerkDef, view: DraftViewLike): void {
  const info = perkCard(def, view.stacks[def.id]);
  card.label.textContent = info.label;
  card.text.textContent = info.text;
  card.owned.textContent = info.owned;
}

function fillCards(ctx: Ctx, view: DraftViewLike): void {
  const keyed = `${view.draft.choices.join("|")}#${view.stageIndex}`;
  if (keyed === ctx.lastKey) return;
  ctx.lastKey = keyed;
  ctx.parts.title.textContent = `STAGE ${view.stageIndex + 1} CLEAR · PICK 1 PERK`;
  ctx.slots = view.draft.choices.map(find);
  for (const index of INDEXES) {
    const card = ctx.parts.cards[index];
    const def = ctx.slots[index];
    if (card === undefined) continue;
    card.button.hidden = def === undefined;
    if (def !== undefined) fillCard(card, def, view);
  }
}

function fillCountdown(ctx: Ctx, view: DraftViewLike): void {
  const countdown = ctx.parts.countdown;
  countdown.hidden = !view.draft.autoPick;
  if (!view.draft.autoPick) return;
  const line = autoPickLine(Math.max(0, AUTO_PICK_MS - view.draft.elapsedMs));
  if (line === ctx.lastCountdown) return;
  ctx.lastCountdown = line;
  countdown.textContent = line;
}

function openPanel(ctx: Ctx, view: DraftViewLike): void {
  ctx.earlier = ctx.deps.doc.activeElement;
  ctx.isShown = true;
  ctx.lastKey = "";
  fillCards(ctx, view);
  ctx.parts.root.hidden = false;
  ctx.deps.doc.addEventListener("keydown", ctx.onKey, true);
  const firstFull = ctx.slots.findIndex((s) => s !== undefined);
  ctx.cursor = Math.max(0, firstFull) as DraftIndex;
  ctx.parts.cards[ctx.cursor]?.button.focus();
}

function closePanel(ctx: Ctx): void {
  ctx.isShown = false;
  ctx.parts.root.hidden = true;
  ctx.deps.doc.removeEventListener("keydown", ctx.onKey, true);
  ctx.lastKey = "";
  ctx.lastCountdown = "";
  const back = ctx.earlier as { focus?: () => void } | null;
  ctx.earlier = null;
  if (back !== null && typeof back.focus === "function") back.focus();
}

function renderPanel(ctx: Ctx, view: DraftViewLike): void {
  ctx.elapsedMs = view.draft.elapsedMs;
  const wanted = view.phase === "draft";
  if (wanted && !ctx.isShown) openPanel(ctx, view);
  else if (!wanted && ctx.isShown) closePanel(ctx);
  if (!ctx.isShown) return;
  fillCards(ctx, view);
  fillCountdown(ctx, view);
}

/**
 * A pad or touch action. The flippers move the cursor at once. The plunger confirms the cursor
 * card, but only after the arm window. The panel takes the action in all cases, so the engine
 * never gets it while the draft shows.
 */
function routeAction(ctx: Ctx, action: InputAction): boolean {
  if (!ctx.isShown) return false;
  if (action === "left_flipper") moveCursor(ctx, -1);
  else if (action === "right_flipper") moveCursor(ctx, 1);
  else if (action === "plunger" && isArmed(ctx)) pick(ctx, ctx.cursor);
  return true;
}

/** Make the nodes of the panel. The cards need the state, so they come in after it. */
function buildParts(ctx: Ctx, doc: DraftDoc): void {
  const { root, title, countdown } = ctx.parts;
  root.setAttribute("role", "dialog");
  root.setAttribute("aria-modal", "false");
  root.setAttribute("aria-labelledby", "draft-title");
  root.hidden = true;
  title.setAttribute("id", "draft-title");
  const odds = makeEl(doc, "p", "draft-odds");
  odds.textContent = ODDS_LINE;
  countdown.hidden = true;
  const row = makeEl(doc, "div", "draft-cards");
  row.append(...ctx.parts.cards.map((c) => c.button));
  root.append(title, row, odds, countdown);
}

/** Make the panel. It builds its nodes once and updates the text only when it changes. */
export function createDraftPanel(deps: DraftPanelDeps): DraftPanel {
  const { doc } = deps;
  const cards: Card[] = [];
  const parts: Parts = {
    root: makeEl(doc, "section", DRAFT_PANEL_CLASS),
    title: makeEl(doc, "h2", "draft-title"),
    countdown: makeEl(doc, "p", "draft-countdown"),
    cards,
  };
  const ctx: Ctx = {
    deps,
    parts,
    onKey: (ev) => handleKey(ctx, ev),
    cursor: 0,
    isShown: false,
    elapsedMs: 0,
    earlier: null,
    slots: [],
    lastKey: "",
    lastCountdown: "",
  };
  for (const index of INDEXES) cards.push(makeCard(ctx, index));
  buildParts(ctx, doc);
  deps.parent.append(parts.root);
  return {
    render: (view) => renderPanel(ctx, view),
    action: (action) => routeAction(ctx, action),
    get shown() {
      return ctx.isShown;
    },
  };
}
